/**
 * Описатели объектов. Модуль для работы с API.
 * @module definitions
 */

import { maxPerPage } from "@/constants";

/**
 * Получение списка описателей объектов. Поддерживает пагинацию, поиск и фильтры
 * @param {Object} params - Параметры запроса
 * @returns {Promise} - список описателей объектов
 * @throws {Error} - ошибка при получении данных
 */
export const getList = async (params) => {
  const queryString = new URLSearchParams(params).toString();

  try {
    const response = await fetch(`/api/definitions?${queryString}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Получение полного списка описателей объектов без пагинации
 * @param {Object} params - Параметры запроса
 * @returns {Promise} - список описателей объектов
 * @throws {Error} - ошибка при получении данных
 */
export const getDefinitionOptions = async (params) => {
  const queryString = new URLSearchParams({
    ...params,
    page: 1,
    perpage: maxPerPage,
  }).toString();

  try {
    const response = await fetch(`/api/definitions?${queryString}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`
      );
    }

    return data.definitions;
  } catch (err) {
    throw err;
  }
};

/**
 * Получение описателя объекта по uuid
 * @param {string} uuid - uuid описателя объекта
 * @returns {Promise} - описатель объекта
 * @throws {Error} - ошибка при получении данных
 */
export const getItem = async (uuid) => {
  try {
    const response = await fetch(`/api/definition?uuid=${uuid}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`
      );
    }

    return data.definition;
  } catch (err) {
    throw err;
  }
};

/**
 * Создание описателя объекта
 * @param {Object} item - данные описателя объекта
 * @returns {Promise} - созданный описатель объекта
 * @throws {Error} - ошибка при создании данных
 */
export const createItem = async (item) => {
  try {
    const response = await fetch("/api/definition", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при создании данных. ${data.error || "Попробуйте снова"}`
      );
    }

    return data.definition;
  } catch (err) {
    throw err;
  }
};

/**
 * Обновление описателя объекта
 * @param {Object} item - данные описателя объекта
 * @returns {Promise} - обновленный описатель объекта
 * @throws {Error} - ошибка при обновлении данных
 */
export const updateItem = async (item) => {
  try {
    const response = await fetch("/api/definition", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при обновлении данных. ${data.error || "Попробуйте снова"}`
      );
    }

    return data.definition;
  } catch (err) {
    throw err;
  }
};

export const addDefinitionTemplates = async (item) => {
  try {
    const response = await fetch("/api/definition/templates", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при добавлении шаблонов. ${data.error || "Попробуйте снова"}`
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

export const deleteDefinitionTemplates = async (item) => {
  try {
    const response = await fetch("/api/definition/templates", {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при удалении шаблонов. ${data.error || "Попробуйте снова"}`
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Удаление описателя объекта
 * @param {Object} item - данные описателя объекта
 * @returns {Promise} - удаленный описатель объекта
 * @throws {Error} - ошибка при удалении данных
 */
export const deleteItem = async (item) => {
  try {
    const response = await fetch("/api/definition", {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при удалении данных. ${data.error || "Попробуйте снова"}`
      );
    }

    return data.definition;
  } catch (err) {
    throw err;
  }
};
